import * as React from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import { CountUp } from '../utility/CountUp';
import { Sparkline } from '../charts/Sparkline';
import { cn } from '../../lib/cn';

export interface KpiCardProps {
  /** Tile label. ReactNode so callers can decorate with icons / tooltips. */
  label: React.ReactNode;
  /** Static value when no CountUp animation desired. */
  value?: React.ReactNode;
  /** Numeric end value — when set, renders via CountUp instead of `value`. */
  end?: number;
  /** CountUp animation duration in ms. Default 1200; auto-bumps to 1400 for hero variant. */
  countDuration?: number;
  delta?: string;
  deltaDir?: 'up' | 'down';
  /** Semantic colour can differ from the directional arrow for adverse metrics. */
  deltaTone?: 'success' | 'danger';
  period?: string;
  spark?: number[];
  sparkColor?: string;
  /** Animate the sparkline draw on mount. Default true. */
  animate?: boolean;
  icon?: IconName;
  /** Optional custom icon node; takes precedence over `icon`. */
  iconNode?: React.ReactNode;
  variant?: 'default' | 'hero';
  /** Compact tiles for dense record-page grids (e.g. Customer 360 dashboard). */
  size?: 'sm' | 'md' | 'lg';
  /** Optional sub-line hint rendered under the value. */
  hint?: React.ReactNode;
  className?: string;
}

export function KpiCard({
  label,
  value,
  end,
  countDuration,
  delta,
  deltaDir = 'up',
  deltaTone,
  period,
  spark,
  sparkColor,
  animate = true,
  icon,
  iconNode,
  variant = 'default',
  size = 'md',
  hint,
  className,
}: KpiCardProps) {
  const duration = countDuration ?? (variant === 'hero' ? 1400 : 1200);
  const labelId = React.useId();

  return (
    <div
      className={cn('kpi', variant === 'hero' && 'hero-kpi', size === 'sm' && 'kpi-sm', size === 'lg' && 'kpi-lg', className)}
      role="group"
      aria-labelledby={labelId}
    >
      <div className="kpi-label" id={labelId}>
        {iconNode || (icon && <Icon name={icon} size={12} />)}
        {label}
      </div>
      <div className="kpi-value tabular">
        {end != null ? <CountUp end={end} duration={duration} /> : value}
      </div>
      {hint ? (
        <div className={cn('kpi-hint', size === 'sm' && 'text-caption')}>{hint}</div>
      ) : null}
      {(delta || period) && (
        <div className="kpi-row">
          {delta && (
            <span className={cn('delta', (deltaTone === 'danger' || (deltaTone === undefined && deltaDir === 'down')) && 'down')}>
              <Icon name={deltaDir === 'down' ? 'arrowDown' : 'arrowUp'} size={10} />
              {delta}
            </span>
          )}
          {period && <span className="delta-period">{period}</span>}
        </div>
      )}
      {spark && spark.length > 0 && (
        <div className="kpi-spark">
          <Sparkline
            data={spark}
            color={sparkColor || 'var(--chart-1)'}
            animate={animate}
          />
        </div>
      )}
    </div>
  );
}
